import { useRef } from 'react'
import { useRequest, useControllableValue } from 'ahooks'

import { Remark } from 'components'
import { Table } from '@sar/component'

import { InputNumber } from 'antd'
import { ProForm, ProFormText, ProFormList, ProFormDependency, ProFormSelect, ProFormDatePicker, ProFormDateTimePicker, ProFormTextArea, ProFormSwitch } from '@ant-design/pro-components'

async function fetchData (params, store) {
  const result = await store.externals.api.get(`/quotations`, {
    params: Object.assign(params, {
      page: params.current,
      size: params.pageSize,
    })
  })

  return {
    success: true,
    data: result.data,
    total: result.totals,
  }
}

export default function ({ store, navigator }) {
  const columnsConfig = [
    {
      title: '序号',
      dataIndex: 'id',
    },
    {
      title: '客户姓名',
      dataIndex: 'customerName',
      formItemProps: () => ({ label: false }),
      fieldProps: () => ({ placeholder: '客户姓名' }),
    },
    {
      title: '联系电话',
      dataIndex: 'customerPhone',
      render: _ => _.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2'),
      formItemProps: () => ({ label: false }),
      fieldProps: () => ({ placeholder: '联系电话' }),
    },
    {
      title: '地址',
      dataIndex: 'customerAddress',
      formItemProps: () => ({ label: false }),
      fieldProps: () => ({ placeholder: '地址' }),
    },
    {
      title: '来源',
      dataIndex: 'sourceId',
      valueType: 'treeSelect',
      hideInTable: true,
      request: () => store.externals.api.get(`/constant/source`),
      formItemProps: () => ({ label: false }),
      fieldProps: () => ({ fieldNames: { value: 'id', label: 'source' }, placeholder: '来源' }),
    },
    {
      title: '来源',
      dataIndex: 'customerSource',
    },
    {
      title: '跟踪员',
      dataIndex: 'saleName',
    },
    {
      title: '报价员',
      dataIndex: 'quoteName',
    },
    {
      title: '报价状态',
      dataIndex: 'quoteStatus',
      valueType: 'select',
      valueEnum: store.config.getConstantEnums('QuotePriceStatus'),
    },
    {
      title: '报价金额',
      dataIndex: 'totalAmount',
      render: (_, data, index, handles) => React.createElement(() => {
        const [ state, dispatch ] = React.useState(data.totalAmount)

        if (store.identity.hasPermission('/price/list:update') == false) {
          return _
        }

        return <InputNumber
          value={state}
          onChange={e => dispatch(e)}
          onBlur={() => store.externals.api.put(`/quotations/${data.id}`, Object.assign(data, { totalAmount: state })).then(() => data.balance = R.defaultTo(0, data.totalAmount) - R.defaultTo(0, data.depositAmount) - R.defaultTo(0, data.advanceAmount))}
        />
      })
    },
    {
      title: '实收订金',
      dataIndex: 'depositAmount',
      render: (_, data, index, handles) => React.createElement(() => {
        const [ state, dispatch ] = React.useState(data.depositAmount)

        if (store.identity.hasPermission('/price/list:update') == false) {
          return _
        }

        return <InputNumber
          value={state}
          onChange={e => dispatch(e)}
          onBlur={() => store.externals.api.put(`/quotations/${data.id}`, Object.assign(data, { depositAmount: state })).then(() => data.balance = R.defaultTo(0, data.totalAmount) - R.defaultTo(0, data.depositAmount) - R.defaultTo(0, data.advanceAmount))}
        />
      })
    },
    {
      title: '实收预付款',
      dataIndex: 'advanceAmount',
      render: (_, data, index, handles) => React.createElement(() => {
        const [ state, dispatch ] = React.useState(data.advanceAmount)

        if (store.identity.hasPermission('/price/list:update') == false) {
          return _
        }

        return <InputNumber
          value={state}
          onChange={e => dispatch(e)}
          onBlur={() => store.externals.api.put(`/quotations/${data.id}`, Object.assign(data, { advanceAmount: state })).then(() => data.balance = R.defaultTo(0, data.totalAmount) - R.defaultTo(0, data.depositAmount) - R.defaultTo(0, data.advanceAmount))}
        />
      })
    },
    {
      title: '应收尾款',
      dataIndex: 'balance',
      render: (_, data, index, handles) => React.createElement(() => {
        const [ state, dispatch ] = React.useState(R.defaultTo(0, data.totalAmount) - R.defaultTo(0, data.depositAmount) - R.defaultTo(0, data.advanceAmount))

        React.useEffect(() => {
          if (Object.getOwnPropertyDescriptor(data, 'balance') == null) {
            Object.defineProperties(data, {
              balance: {
                set: function (value) {
                  dispatch(value)
                },
              },
            })
          }
        }, [])

        return state == null ? '-' : state < 0 ? 0 : state
      })
    },
    {
      title: '生产类型',
      dataIndex: 'type',
      valueType: 'select',
      valueEnum: store.config.getConstantEnums('OrderType'),
    },
    {
      title: '收款凭证上传',
      dataIndex: 'license',
      render: (_, data) => <a className='text-blue-600' onClick={() => navigator.to(`/price/list/receipt/${data.id}`, `收款凭证上传`)}>{data.upReceiptVoucher ? '查看' : '上传'}</a>,
    },
    {
      title: '更新时间',
      dataIndex: 'updateTime',
      valueType: 'date',
    },
  ]

  const rowActions = [
    {
      danger: true,
      name: '下单',
      confirmType: 'modal',
      confirmMessage: '下单后不可撤销，订单进入拆单环节',
      visible: () => store.identity.hasPermission('/price/list:submit'),
      onConfirm: (data, handles) => store.externals.api.put(`/quotations/submit/${data.id}`).then(() => handles.current.reload()),
    },
    {
      name: '查看备注',
      form: props => <Remark {...props}/>,
    },
    {
      danger: true,
      name: '删除',
      confirmType: 'modal',
      confirmMessage: '您确定删除这个报价单吗？',
      visible: () => store.identity.hasPermission('/price/list:delete'),
      onConfirm: (data, handles) => store.externals.api.delete(`/quotations/${data.id}`).then(() => handles.current.reload()),
    },
  ]

  const columns = React.useMemo(() => {
    return columnsConfig.map(column => {
      let keys = [
          'customerName'
        , 'customerPhone'
        , 'customerAddress'
        , 'sourceId'
      ]

      return keys.includes(column.dataIndex) ? column : Object.assign(column, { hideInSearch: true })
    })
  }, [
    columnsConfig,
  ])

  return <div className='p-4'>
    <Table
      title='报价列表'
      columns={columns}
      rowActions={rowActions}
      request={params => fetchData(params, store)}
      scroll={{ }}
      search={{ labelWidth: 'auto', defaultCollapsed: true, span: Object.assign({}, store.config.responsive, { xxl: 4 }) }}
    />
  </div>
}
